import { CallResult, doApiGet, doApiPatch, doApiPost } from "./Requests";
import {
  CreateTimeTrackingRequest,
  CreateTimeQuotaRequest,
  CurrentTimeQuota,
  TimeQuota,
  TimeQuotaBalance,
  TimeTrackingEntry,
  UpdateTimeQuotaRequest,
} from "../types/timeTracking";
import { PaginationParameters, PaginationResult, toQueryParameters } from "./Pagination";

type TimeRangeFilter = {
  startTimestamp?: string | null;
  endTimestamp?: string | null;
};

type CompanyFilter = TimeRangeFilter & {
  employeeUserId?: string | null;
  approval?: string | null;
};

const buildQueryString = (params: Record<string, string | undefined>) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value && value.length > 0) {
      searchParams.append(key, value);
    }
  });
  const query = searchParams.toString();
  return query.length > 0 ? `?${query}` : "";
};

export const timeTrackingApi = {
  createEntry: (
    serviceModuleId: number,
    payload: CreateTimeTrackingRequest
  ): Promise<CallResult<unknown>> => {
    return doApiPost<CreateTimeTrackingRequest, unknown>(
      `/api/companies/current/servicemodules/timetracking/${serviceModuleId}/timetracking`,
      payload
    );
  },

  createTimeQuota: (
    serviceModuleId: number,
    payload: CreateTimeQuotaRequest
  ): Promise<CallResult<unknown>> => {
    return doApiPost<CreateTimeQuotaRequest, unknown>(
      `/api/companies/current/servicemodules/timetracking/${serviceModuleId}/timequota`,
      payload
    );
  },

  getTimeQuotas: (
    serviceModuleId: number,
    employeeId: string
  ): Promise<CallResult<TimeQuota[]>> => {
    const query = buildQueryString({ employeeId });
    return doApiGet<TimeQuota[]>(
      `/api/companies/current/servicemodules/timetracking/${serviceModuleId}/timequota${query}`
    );
  },

  getCurrentTimeQuota: (
    serviceModuleId: number,
    employeeId: string
  ): Promise<CallResult<CurrentTimeQuota>> => {
    const query = buildQueryString({ employeeId });
    return doApiGet<CurrentTimeQuota>(
      `/api/companies/current/servicemodules/timetracking/${serviceModuleId}/timequota/current${query}`
    );
  },

  getTimeQuotaBalance: (
    serviceModuleId: number,
    employeeId: string
  ): Promise<CallResult<TimeQuotaBalance>> => {
    const query = buildQueryString({ employeeId });
    return doApiGet<TimeQuotaBalance>(
      `/api/companies/current/servicemodules/timetracking/${serviceModuleId}/timequota/balance${query}`
    );
  },

  getUserEntries: (
    serviceModuleId: number,
    filters: TimeRangeFilter = {}
  ): Promise<CallResult<TimeTrackingEntry[]>> => {
    const query = buildQueryString({
      startTimestamp: filters.startTimestamp ?? undefined,
      endTimestamp: filters.endTimestamp ?? undefined,
    });
    return doApiGet<TimeTrackingEntry[]>(
      `/api/companies/current/servicemodules/timetracking/${serviceModuleId}/timetracking${query}`
    );
  },

  getCompanyEntries(
    serviceModuleId: number,
    params: PaginationParameters & CompanyFilter
  ): Promise<CallResult<PaginationResult<TimeTrackingEntry>>> {
    const {
      offset,
      limit,
      search,
      sort,
      order,
      employeeUserId,
      startTimestamp,
      endTimestamp,
      approval,
    } = params;
    const paginationQuery = toQueryParameters({
      offset,
      limit,
      search,
      sort,
      order,
    });

    const filterQuery = buildQueryString({
      employeeUserId: employeeUserId ?? undefined,
      startTimestamp: startTimestamp ?? undefined,
      endTimestamp: endTimestamp ?? undefined,
      approval: approval ?? undefined,
    });

    return doApiGet<PaginationResult<TimeTrackingEntry>>(
      `/api/companies/current/servicemodules/timetracking/${serviceModuleId}/timetracking/company` +
      `?${paginationQuery}${filterQuery.replace("?", "&")}`
    );
  },

  updateTimeTrackingApproval: (
    serviceModuleId: number,
    id: number,
    approval: "Pending" | "Approved" | "Rejected" | "Invalid"
  ): Promise<CallResult<TimeTrackingEntry>> => {
    return doApiPatch<{ approval: string }, TimeTrackingEntry>(
      `/api/companies/current/servicemodules/timetracking/${serviceModuleId}/timetracking/${id}`,
      { approval }
    );
  },

  updateTimeQuota: (
    serviceModuleId: number,
    quotaId: number,
    employeeId: string,
    payload: UpdateTimeQuotaRequest
  ): Promise<CallResult<TimeQuota>> => {
    return doApiPatch<UpdateTimeQuotaRequest, TimeQuota>(
      `/api/companies/current/servicemodules/timetracking/${serviceModuleId}/timequota` +
      `?id=${quotaId}&employeeId=${employeeId}`,
      payload
    );
  },
};
